// Per-customer pricing precedence used across the POS catalog/cart/modal.
//
// Mirrors the legacy POS card pricing pattern (waio-portal
// index_3.html:3673-3685):
//   - `regular_price` is the main bold number on the card
//   - `price2` shows as the black "member" pill when set and below regular
//   - `scheduled_discount.discount_price` shows as the red "sale" pill when
//     an active schedule applies; takes precedence over the member pill
//
// `effectiveUnitPrice` returns the single number the cashier/customer
// actually pays per unit (lowest of the three when multiple are set);
// `getPriceDisplay` returns the breakdown for UI components that render
// pills/strikethroughs side-by-side.
//
// The CFD (`routes/customer.tsx`) doesn't compute precedence — POS
// resolves the customer's effective price upstream and ships it as
// `effective_unit_price` on the wire (see `cartChannel.ts`). POS picks
// /cart/preview's server-resolved price when the product is in the cart,
// or `effectiveUnitPrice(product, customer)` otherwise, so the CFD's
// displayed price always matches the cashier's Add-to-Cart button.

import type { Product } from '@/types'

function toNum(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : parseFloat(String(value))
  return Number.isFinite(n) ? n : null
}

export interface PriceDisplay {
  /** Bold catalog price — `regular_price` if set, else `price`. */
  regular: number
  /** Red "sale" pill — `scheduled_discount.discount_price` when below regular. */
  salePill: number | null
  /** Black "member" pill — `price2` when below regular and no sale wins. */
  memberPill: number | null
}

export function getPriceDisplay(product: Product): PriceDisplay {
  const fallback = toNum(product.price) ?? 0
  const regular = toNum(product.regular_price) ?? fallback
  const price2 = toNum(product.price2)
  const sale = toNum(product.scheduled_discount?.discount_price ?? null)

  const salePill = sale != null && sale > 0 && sale < regular ? sale : null
  const memberPill =
    salePill === null && price2 != null && price2 > 0 && price2 !== regular
      ? price2
      : null

  return { regular, salePill, memberPill }
}

/**
 * Lowest applicable per-unit price for the active cashier context: regular,
 * the member-tier `price2` (only when a customer with a `price_tier` is
 * selected), or an active scheduled-discount sale. Used by
 * `posStore.addToCart` for the optimistic cart-line shelf price and by
 * `ProductModal` for its qty math.
 *
 * The server `/cart/preview` is the authority — this function is the
 * local-only price before the preview returns, and the fallback when a
 * priced lookup misses.
 *
 * `price2` is the member tier price. The BE only applies it server-side
 * when the customer qualifies, but it's still set on the Product record
 * for everyone (legacy waio-portal renders it as a black "member" pill on
 * the card). If we always picked `min(regular, price2, sale)` the FE
 * would optimistically show the member price to walk-ins, then visibly
 * snap up to the regular price when the preview returned. Gating on
 * `customer.price_tier` mirrors the BE's eligibility check closely
 * enough that the optimistic estimate matches the preview for the
 * common cases.
 */
export function effectiveUnitPrice(
  product: Product,
  customer?: { price_tier?: string | null } | null,
): number {
  const fallback = toNum(product.price) ?? 0
  const regular = toNum(product.regular_price) ?? fallback
  const price2 = toNum(product.price2)
  const sale = toNum(product.scheduled_discount?.discount_price ?? null)

  const candidates: number[] = []
  if (regular > 0) candidates.push(regular)
  const customerHasTier = !!customer?.price_tier
  if (customerHasTier && price2 != null && price2 > 0) candidates.push(price2)
  if (sale != null && sale > 0) candidates.push(sale)

  return candidates.length > 0 ? Math.min(...candidates) : fallback
}
